import { Fragment, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Container, Input, Modal, ModalHeader, ModalBody, ModalFooter, Label } from "reactstrap";
import { getKdsDevices, approveKdsDevice, updateKdsDevice, updateKdsDeviceLayout, revokeKdsDevice, deleteKdsDevice, fetchBranches } from "../../utils/apiCalls";
import { errorToast } from "../../shared/Toast";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Badge, Loader } from "../reporting/ReportingShared";
import Breadcrumb from "../common/breadcrumb";
import KdsStationSection from "./kds-station-section";

const BRAND = "#009689"; // web KDS branded-panel color when no color is set
const DISPLAY_TEXT_MAX = 60; // mirrors the API's util/kdsDisplayLayout.js
const assetUrl = (path) => (!path ? null : /^(https?:|data:|blob:)/.test(path) ? path : process.env.REACT_APP_URL + path);

const THEME_DEFAULTS = {
  pageBg: "#F7F9FD",
  preparing: { fontSize: "", color: "#525866", bg: "#FFFFFF", border: "#EBEEF7" },
  ready: { fontSize: "", color: "#FFFFFF", bg: "#175A7D", border: "" },
  logo: { show: true, height: 80 },
};
const EMPTY_THEME = {
  pageBg: "",
  preparing: { fontSize: "", color: "", bg: "", border: "" },
  ready: { fontSize: "", color: "", bg: "", border: "" },
  logo: { show: true, height: 80 },
};
const FONT_SIZE_RANGE = [32, 200];
const LOGO_HEIGHT_RANGE = [40, 300];

// Saved theme → form (null/undefined → "" so every input is controlled).
const themeToForm = (t) => ({
  pageBg: t?.pageBg || "",
  preparing: { fontSize: t?.preparing?.fontSize ?? "", color: t?.preparing?.color || "", bg: t?.preparing?.bg || "", border: t?.preparing?.border || "" },
  ready: { fontSize: t?.ready?.fontSize ?? "", color: t?.ready?.color || "", bg: t?.ready?.bg || "", border: t?.ready?.border || "" },
  logo: { show: t?.logo?.show !== false, height: t?.logo?.height ?? 80 },
});

// Form → what the API stores ("" → null = default). Sent as one JSON string in the multipart form.
const formToTheme = (f) => {
  const tile = (s) => ({ fontSize: s.fontSize === "" ? null : Number(s.fontSize), color: s.color || null, bg: s.bg || null, border: s.border || null });
  return { pageBg: f.pageBg || null, preparing: tile(f.preparing), ready: tile(f.ready), logo: { show: !!f.logo.show, height: Number(f.logo.height) || 80 } };
};

// The form value with defaults filled in — what the screen will actually render.
const resolveTile = (form, defaults) => ({
  fontSize: form.fontSize, color: form.color || defaults.color, bg: form.bg || defaults.bg, border: form.border || defaults.border,
});

const KdsDeviceList = () => {
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? merchantId : reduxSelectedMerchant;

  const [devices, setDevices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [approveModal, setApproveModal] = useState(null); // the device being approved/reassigned
  const [approveForm, setApproveForm] = useState({ branchId: "", role: "display", name: "" });
  const [editModal, setEditModal] = useState(null); // rename / change role — keeps the same key
  const [editForm, setEditForm] = useState({ name: "", role: "display" });
  const [layoutModal, setLayoutModal] = useState(null); // Display-screen layout (columns / branded)
  const [layoutForm, setLayoutForm] = useState({ mode: "columns", text: "", bgColor: "", image: null, theme: EMPTY_THEME });
  const [layoutFile, setLayoutFile] = useState(null);
  const [layoutSaving, setLayoutSaving] = useState(false);

  const fetchDevices = useCallback(async () => {
    if (!selectedMerchant || selectedMerchant === "all") { setDevices([]); return; }
    const data = await getKdsDevices(selectedMerchant);
    setDevices(data || []);
  }, [selectedMerchant]);

  useEffect(() => {
    setIsLoading(true);
    fetchDevices().finally(() => setIsLoading(false));
  }, [fetchDevices]);

  // Keep online status + pending devices fresh.
  useEffect(() => {
    const id = setInterval(() => { fetchDevices(); }, 10000);
    return () => clearInterval(id);
  }, [fetchDevices]);

  useEffect(() => {
    const load = async () => {
      const response = await fetchBranches({}, false);
      let list = response?.data || [];
      if (selectedMerchant && selectedMerchant !== "all") {
        list = list.filter((b) => String(b.MerchantId) === String(selectedMerchant));
      }
      setBranches(list);
    };
    load();
  }, [selectedMerchant]);

  const openApprove = (device) => {
    setApproveForm({ branchId: device.branchPk || "", role: device.role || "display", name: device.name || "" });
    setApproveModal(device);
  };

  const handleApprove = async () => {
    if (!approveForm.branchId) { errorToast("Select a branch"); return; }
    const result = await approveKdsDevice(approveModal.id, {
      branchId: Number(approveForm.branchId),
      role: approveForm.role,
      name: approveForm.name || null,
    });
    if (result) { setApproveModal(null); fetchDevices(); }
  };

  const openEdit = (device) => {
    setEditForm({ name: device.name || "", role: device.role || "display" });
    setEditModal(device);
  };

  const handleEdit = async () => {
    const result = await updateKdsDevice(editModal.id, {
      name: editForm.name || null,
      role: editForm.role,
    });
    if (result) { setEditModal(null); fetchDevices(); }
  };

  const openLayout = (device) => {
    const l = device.displayLayout || {};
    setLayoutForm({
      mode: l.mode === "branded" ? "branded" : "columns",
      text: l.text || "",
      bgColor: l.bgColor || "",
      image: l.image || null,
      theme: themeToForm(l.theme),
    });
    setLayoutFile(null);
    setLayoutModal(device);
  };

  // Patch one theme field: setTheme("preparing", "color", "#112233") or setTheme("pageBg", null, "#fff").
  const setTheme = (section, key, value) =>
    setLayoutForm((p) => ({
      ...p,
      theme: key == null ? { ...p.theme, [section]: value } : { ...p.theme, [section]: { ...p.theme[section], [key]: value } },
    }));

  const handleLayoutSave = async () => {
    const t = layoutForm.theme;
    const badSize = ["preparing", "ready"].find((s) => t[s].fontSize !== "" && (Number(t[s].fontSize) < FONT_SIZE_RANGE[0] || Number(t[s].fontSize) > FONT_SIZE_RANGE[1]));
    if (badSize) { errorToast(`${badSize === "ready" ? "Ready" : "Preparing"} font size must be between ${FONT_SIZE_RANGE[0]} and ${FONT_SIZE_RANGE[1]} px`); return; }
    if (Number(t.logo.height) < LOGO_HEIGHT_RANGE[0] || Number(t.logo.height) > LOGO_HEIGHT_RANGE[1]) { errorToast(`Logo height must be between ${LOGO_HEIGHT_RANGE[0]} and ${LOGO_HEIGHT_RANGE[1]} px`); return; }

    setLayoutSaving(true);
    const result = await updateKdsDeviceLayout(
      layoutModal.id,
      {
        displayMode: layoutForm.mode,
        displayText: layoutForm.text.trim(),
        displayBgColor: layoutForm.bgColor,
        removeDisplayImage: !layoutForm.image && !layoutFile ? "true" : "false",
        displayTheme: JSON.stringify(formToTheme(t)),
      },
      layoutFile
    );
    setLayoutSaving(false);
    if (result) { setLayoutModal(null); fetchDevices(); }
  };

  // Object URL for the picked file, revoked when it changes so previews don't leak.
  const [layoutFileUrl, setLayoutFileUrl] = useState(null);
  useEffect(() => {
    if (!layoutFile) { setLayoutFileUrl(null); return; }
    const url = URL.createObjectURL(layoutFile);
    setLayoutFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [layoutFile]);

  const handleRevoke = async (id) => {
    if (!window.confirm("Revoke this device's access? The screen will be signed out.")) return;
    if (await revokeKdsDevice(id)) fetchDevices();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this device from the list?")) return;
    if (await deleteKdsDevice(id)) fetchDevices();
  };

  const statusBadge = (s) => {
    if (s === "active") return { color: C.green, bg: C.greenLight };
    if (s === "pending") return { color: C.orange, bg: C.orangeLight };
    return { color: C.coral, bg: C.coralLight };
  };

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title="KDS Devices" parent="KDS Devices" />
      <Container fluid className="rpt-wrap">
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20 }}>
          {(!selectedMerchant || selectedMerchant === "all") && (
            <span style={{ color: C.orange, fontWeight: 600, fontSize: 13 }}>Please select a merchant from the dropdown</span>
          )}
          <div style={{ flex: 1 }} />
          <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600, fontSize: 12, padding: "8px 16px" }} onClick={() => fetchDevices()}>
            ↻ Refresh
          </Button>
        </div>

        {/* Two different things share this page because staff think of them as one question — "what
            kitchen screens does this branch have?" — but they are not interchangeable. Web KDS screens
            pair themselves and hold an API key; LAN stations are displays on the shop's own network
            that a POS registers and a self-order screen pushes tickets to over TCP. */}
        <div style={{ fontWeight: 700, fontSize: 15, color: C.text, marginBottom: 4 }}>Web KDS screens</div>
        <div style={{ fontSize: 12, color: C.textLight, marginBottom: 14 }}>
          Browser-based kitchen boards. They pair themselves and appear here for approval.
        </div>

        <RCard style={{ padding: 0, overflow: "hidden" }}>
          {isLoading ? <Loader /> : devices.length === 0 ? (
            <p style={{ textAlign: "center", padding: 40, color: C.textLight }}>
              {(!selectedMerchant || selectedMerchant === "all")
                ? "Select a merchant"
                : "No KDS screens yet — open the web KDS on a screen and its pairing code will appear here."}
            </p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={tbl.table}>
                <thead>
                  <tr>
                    {["Device", "Role", "Layout", "Branch", "Status", "Live", "Pairing / Last seen", "Actions"].map((h) => (
                      <th key={h} style={{ ...tbl.th, ...(h === "Actions" && { textAlign: "right" }) }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {devices.map((d) => {
                    const sb = statusBadge(d.status);
                    return (
                      <tr key={d.id} className="rpt-row">
                        <td style={tbl.td}>
                          <div style={{ fontWeight: 600, color: C.text }}>{d.name || "Unnamed screen"}</div>
                          <code style={{ fontSize: 11, color: C.textLight }}>{String(d.deviceId).length > 12 ? `${String(d.deviceId).slice(0, 12)}…` : String(d.deviceId)}</code>
                        </td>
                        <td style={tbl.td}>{d.role === "controller" ? "Controller" : "Display"}</td>
                        <td style={tbl.td}>
                          {d.role === "controller"
                            ? <span style={{ color: C.textLight }}>—</span>
                            : d.displayLayout?.mode === "branded" ? "Branded" : "Two columns"}
                        </td>
                        <td style={tbl.td}>{d.branchId || <span style={{ color: C.textLight }}>—</span>}</td>
                        <td style={tbl.td}><Badge color={sb.color} bg={sb.bg}>{d.status}</Badge></td>
                        <td style={tbl.td}>
                          {d.status === "active" ? (
                            <Badge color={d.online ? C.green : C.textLight} bg={d.online ? C.greenLight : C.bg}>
                              {d.online ? "● Online" : "○ Off"}
                            </Badge>
                          ) : <span style={{ color: C.textLight }}>—</span>}
                        </td>
                        <td style={tbl.td}>
                          {d.status === "pending"
                            ? <code style={{ fontSize: 16, fontWeight: 700, letterSpacing: 3, color: C.coral }}>{d.pairingCode}</code>
                            : (d.lastSeenAt ? new Date(d.lastSeenAt).toLocaleString() : <span style={{ color: C.textLight }}>Never</span>)}
                        </td>
                        <td style={{ ...tbl.td, textAlign: "right" }}>
                          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                            {d.status === "pending" && (
                              <Button size="sm" style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, color: "#fff" }} onClick={() => openApprove(d)}>Approve</Button>
                            )}
                            {d.status === "active" && (
                              <>
                                <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMed, fontSize: 11, fontWeight: 700 }} onClick={() => openEdit(d)}>Edit</Button>
                                {d.role !== "controller" && (
                                  <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMed, fontSize: 11, fontWeight: 700 }} onClick={() => openLayout(d)}>Layout</Button>
                                )}
                                <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMed, fontSize: 11, fontWeight: 700 }} onClick={() => openApprove(d)}>Reassign</Button>
                                <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.coral, fontSize: 11, fontWeight: 700 }} onClick={() => handleRevoke(d.id)}>Revoke</Button>
                              </>
                            )}
                            <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textLight, fontSize: 11, fontWeight: 700 }} onClick={() => handleDelete(d.id)}>Delete</Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </RCard>

        <KdsStationSection selectedMerchant={selectedMerchant} branches={branches} />
      </Container>

      {/* Approve / reassign modal */}
      <Modal isOpen={!!approveModal} toggle={() => setApproveModal(null)}>
        <ModalHeader toggle={() => setApproveModal(null)}>
          {approveModal?.status === "active" ? "Reassign KDS device" : "Approve KDS device"}
        </ModalHeader>
        <ModalBody>
          <Label>Branch *</Label>
          <Input type="select" value={approveForm.branchId} onChange={(e) => setApproveForm((p) => ({ ...p, branchId: e.target.value }))} style={{ borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 12 }}>
            <option value="">Select a branch</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Input>
          <Label>Role *</Label>
          <Input type="select" value={approveForm.role} onChange={(e) => setApproveForm((p) => ({ ...p, role: e.target.value }))} style={{ borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 12 }}>
            <option value="display">Display (read-only)</option>
            <option value="controller">Controller (interactive)</option>
          </Input>
          <Label>Name (optional)</Label>
          <Input type="text" value={approveForm.name} onChange={(e) => setApproveForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Grill screen" style={{ borderRadius: 8, border: `1px solid ${C.border}` }} />
          <div style={{ fontSize: 12, color: C.textLight, marginTop: 10 }}>
            {approveModal?.status === "active"
              ? "Reassigning mints a NEW branch-scoped key (the old one is revoked) — the screen reconnects automatically. To just rename or change the role, use Edit instead."
              : "Approving mints a branch-scoped KDS key and binds it to this screen — it will connect automatically."}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed }} onClick={() => setApproveModal(null)}>Cancel</Button>
          <Button style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600 }} onClick={handleApprove}>Approve &amp; assign key</Button>
        </ModalFooter>
      </Modal>

      {/* Edit modal — rename / change role without re-keying the screen */}
      <Modal isOpen={!!editModal} toggle={() => setEditModal(null)}>
        <ModalHeader toggle={() => setEditModal(null)}>Edit KDS device</ModalHeader>
        <ModalBody>
          <Label>Name</Label>
          <Input type="text" value={editForm.name} onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Grill screen" style={{ borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 12 }} />
          <Label>Role</Label>
          <Input type="select" value={editForm.role} onChange={(e) => setEditForm((p) => ({ ...p, role: e.target.value }))} style={{ borderRadius: 8, border: `1px solid ${C.border}` }}>
            <option value="display">Display (read-only)</option>
            <option value="controller">Controller (interactive)</option>
          </Input>
          <div style={{ fontSize: 12, color: C.textLight, marginTop: 10 }}>
            Keeps the same key — the screen stays connected.
          </div>
        </ModalBody>
        <ModalFooter>
          <Button style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed }} onClick={() => setEditModal(null)}>Cancel</Button>
          <Button style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600 }} onClick={handleEdit}>Save</Button>
        </ModalFooter>
      </Modal>

      {/* Display layout modal — what this customer-facing screen shows. Applies live (no re-key). */}
      <Modal isOpen={!!layoutModal} toggle={() => setLayoutModal(null)} size="lg">
        <ModalHeader toggle={() => setLayoutModal(null)}>Display layout — {layoutModal?.name || "Unnamed screen"}</ModalHeader>
        {layoutModal && (() => {
          const fallback = layoutModal.merchantDisplay || {};
          const branchName = branches.find((b) => String(b.id) === String(layoutModal.branchPk))?.name || layoutModal.branchId || "Branch name";
          const branded = layoutForm.mode === "branded";
          const image = layoutFileUrl || assetUrl(layoutForm.image) || assetUrl(fallback.image);
          const usingMerchantImage = !layoutFileUrl && !layoutForm.image && !!fallback.image;
          // Branded panel color: the screen's own, else the merchant's, else teal (mirrors the web board).
          const color = layoutForm.bgColor || fallback.bgColor || BRAND;
          const th = layoutForm.theme;
          const prep = resolveTile(th.preparing, THEME_DEFAULTS.preparing);
          const rdy = resolveTile(th.ready, THEME_DEFAULTS.ready);
          const pageBg = th.pageBg || THEME_DEFAULTS.pageBg;
          const logoUrl = th.logo.show ? assetUrl(fallback.logo) : null;
          // Preview is ~1/8 of a 1080p screen: scale fixed px sizes down, else use a preview default.
          const chipFont = (tile, big) => (tile.fontSize ? Math.max(9, Math.round(Number(tile.fontSize) / 8)) : big ? 15 : 13);
          const chip = (n, tile, big) => (
            <span key={n} style={{
              fontWeight: 700, fontSize: chipFont(tile, big), lineHeight: 1.2, padding: "1px 7px", borderRadius: 6,
              color: tile.color, background: tile.bg || "transparent", border: `1px solid ${tile.border || "transparent"}`,
            }}>{n}</span>
          );
          const chips = (list, tile, big) => (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, justifyContent: "center", padding: 8 }}>{list.map((n) => chip(n, tile, big))}</div>
          );
          // Header drawn like a big chip of its section, English + Arabic together — as on the screen.
          const head = (en, ar, tile) => (
            <div style={{ textAlign: "center", fontWeight: 600, padding: "5px 0", fontSize: 11, lineHeight: 1.3, color: tile.color, background: tile.bg || "transparent", borderBottom: `1px solid ${tile.border || "transparent"}` }}>
              <span dir="rtl" style={{ fontSize: 14, fontWeight: 700 }}>{ar}</span><br />{en}
            </div>
          );
          const colorRow = (label, value, defaultHex, onChange, hint) => (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 3 }}>{label}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Input type="color" value={value || defaultHex || "#ffffff"} onChange={(e) => onChange(e.target.value)} style={{ width: 44, height: 30, padding: 2, borderRadius: 6 }} />
                <code style={{ fontSize: 11, color: C.textMed }}>{value || (defaultHex ? `default ${defaultHex}` : "none")}</code>
                {value && <Button size="sm" color="link" style={{ fontSize: 11, padding: 0 }} onClick={() => onChange("")}>Default</Button>}
              </div>
              {hint && <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>{hint}</div>}
            </div>
          );
          const tileCard = (section, title) => {
            const f = th[section];
            const d = THEME_DEFAULTS[section];
            return (
              <div style={{ flex: 1, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 10 }}>{title}</div>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 3 }}>Font size (px)</div>
                  <Input type="number" min={FONT_SIZE_RANGE[0]} max={FONT_SIZE_RANGE[1]} value={f.fontSize} placeholder="auto" onChange={(e) => setTheme(section, "fontSize", e.target.value)} style={{ borderRadius: 6, border: `1px solid ${C.border}`, height: 30, fontSize: 12, width: 110 }} />
                  <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>Empty = fits the screen automatically.</div>
                </div>
                {colorRow("Number color", f.color, d.color, (v) => setTheme(section, "color", v))}
                {colorRow("Background", f.bg, d.bg, (v) => setTheme(section, "bg", v))}
                {colorRow("Border", f.border, d.border, (v) => setTheme(section, "border", v))}
              </div>
            );
          };
          const modeCard = (mode, title, sub) => (
            <div
              onClick={() => setLayoutForm((p) => ({ ...p, mode }))}
              style={{
                flex: 1, cursor: "pointer", borderRadius: 10, padding: "12px 14px",
                border: `2px solid ${layoutForm.mode === mode ? C.coral : C.border}`,
                background: layoutForm.mode === mode ? "#f0fdfa" : C.white,
              }}
            >
              <div style={{ fontWeight: 700, color: C.text, fontSize: 14 }}>{title}</div>
              <div style={{ fontSize: 12, color: C.textLight, marginTop: 2 }}>{sub}</div>
            </div>
          );
          return (
            <>
              <ModalBody>
                <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                  {modeCard("columns", "Two columns", "Preparing on the left, Ready on the right. Default.")}
                  {modeCard("branded", "Branded panel", "Image or text panel on the left, numbers on the right.")}
                </div>

                {/* Static preview of the chosen layout (~1/8 scale of a 1080p screen) */}
                <div style={{ position: "relative", display: "flex", height: 190, borderRadius: 10, overflow: "hidden", border: `1px solid ${C.border}`, marginBottom: 18, background: branded ? "#fff" : pageBg }}>
                  {branded ? (
                    <>
                      <div style={{ width: "45%", background: color, display: "grid", placeItems: "center", overflow: "hidden" }}>
                        {image
                          ? <img src={image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          : <span style={{ fontWeight: 700, fontSize: 16, padding: 10, textAlign: "center", color: "#fff", textShadow: "0 1px 2px rgba(0,0,0,.25)" }}>{layoutForm.text.trim() || branchName}</span>}
                      </div>
                      <div style={{ flex: 1, padding: "8px 12px" }}>
                        {head("Ready", "جاهز", rdy)}
                        {chips([12, 15], rdy, true)}
                        <div style={{ height: 1, background: C.border, margin: "2px 0" }} />
                        {head("In Progress", "قيد التحضير", prep)}
                        {chips([16, 17, 18], prep)}
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ flex: 1 }}>
                        {head("Preparing", "قيد التحضير", prep)}
                        {chips([16, 17, 18, 19], prep)}
                      </div>
                      <div style={{ width: 1, background: prep.border || "transparent" }} />
                      <div style={{ flex: 1 }}>
                        {head("Ready", "جاهز", rdy)}
                        {chips([12, 15], rdy, true)}
                      </div>
                    </>
                  )}
                  {logoUrl && <img src={logoUrl} alt="" style={{ position: "absolute", right: 6, bottom: 6, height: Math.max(8, Math.round(Number(th.logo.height) / 8)), width: "auto", maxWidth: "40%", objectFit: "contain" }} />}
                </div>

                {branded ? (
                  <>
                    <Label>Panel background color</Label>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                      <Input type="color" value={layoutForm.bgColor || color} onChange={(e) => setLayoutForm((p) => ({ ...p, bgColor: e.target.value }))} style={{ width: 56, height: 36, padding: 2, borderRadius: 8 }} />
                      <code style={{ fontSize: 12, color: C.textMed }}>{layoutForm.bgColor || "default"}</code>
                      {layoutForm.bgColor && (
                        <Button size="sm" color="link" style={{ fontSize: 12, padding: 0 }} onClick={() => setLayoutForm((p) => ({ ...p, bgColor: "" }))}>Reset</Button>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: C.textLight, marginBottom: 14 }}>
                      {layoutForm.bgColor ? "This screen's own color." : fallback.bgColor ? `Using the merchant color (${fallback.bgColor}).` : "Using the default teal."}
                    </div>
                  </>
                ) : (
                  colorRow("Page background", th.pageBg, THEME_DEFAULTS.pageBg, (v) => setTheme("pageBg", null, v), "Whole-screen background behind both columns.")
                )}

                <div style={{ fontWeight: 700, fontSize: 13, color: C.text, margin: "6px 0 8px" }}>Numbers</div>
                <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                  {tileCard("preparing", branded ? "In Progress" : "Preparing")}
                  {tileCard("ready", "Ready")}
                </div>

                <div style={{ fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 8 }}>Merchant logo (bottom-right)</div>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 4 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, margin: 0, cursor: "pointer" }}>
                    <input type="checkbox" checked={th.logo.show} onChange={(e) => setTheme("logo", "show", e.target.checked)} /> Show
                  </label>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                    Height
                    <Input type="number" min={LOGO_HEIGHT_RANGE[0]} max={LOGO_HEIGHT_RANGE[1]} value={th.logo.height} disabled={!th.logo.show} onChange={(e) => setTheme("logo", "height", e.target.value)} style={{ borderRadius: 6, border: `1px solid ${C.border}`, height: 30, fontSize: 12, width: 80 }} />
                    px
                  </div>
                </div>
                <div style={{ fontSize: 12, color: C.textLight, marginBottom: 14 }}>
                  {fallback.logo
                    ? "Uses the merchant's image from Edit Merchant → Merchant Image."
                    : "This merchant has no image yet — upload one in Edit Merchant → Merchant Image, otherwise nothing is shown."}
                </div>

                {branded && (
                  <>
                    <Label>Panel image (optional)</Label>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                      <Input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setLayoutFile(e.target.files?.[0] || null)} style={{ borderRadius: 8, border: `1px solid ${C.border}` }} />
                      {(layoutFile || layoutForm.image) && (
                        <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.coral, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}
                          onClick={() => { setLayoutFile(null); setLayoutForm((p) => ({ ...p, image: null })); }}>
                          ✕ Remove
                        </Button>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: C.textLight, marginBottom: 14 }}>
                      {usingMerchantImage
                        ? "Using the merchant's display image. Upload one to override it for this screen."
                        : "When an image is set it fills the panel and the text below is not shown."}
                    </div>

                    <Label>Panel text</Label>
                    <Input type="text" maxLength={DISPLAY_TEXT_MAX} value={layoutForm.text} onChange={(e) => setLayoutForm((p) => ({ ...p, text: e.target.value }))} placeholder={branchName} style={{ borderRadius: 8, border: `1px solid ${C.border}` }} />
                    <div style={{ fontSize: 12, color: C.textLight, marginTop: 4 }}>Leave empty to show the branch name.</div>
                  </>
                )}
              </ModalBody>
              <ModalFooter>
                <span style={{ fontSize: 12, color: C.textLight, marginRight: "auto" }}>The screen updates right away. No re-pairing needed.</span>
                <Button style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed }} onClick={() => setLayoutModal(null)}>Cancel</Button>
                <Button disabled={layoutSaving} style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600 }} onClick={handleLayoutSave}>
                  {layoutSaving ? "Saving…" : "Save layout"}
                </Button>
              </ModalFooter>
            </>
          );
        })()}
      </Modal>
    </Fragment>
  );
};

export default KdsDeviceList;
